import yaml from 'js-yaml';

export const CORE_SECRET_TYPE = 'core';

type ServerNode = {
  secrets?: { type?: string; secret_name?: string; [key: string]: unknown };
  [key: string]: unknown;
};

type ContractDoc = {
  servers?: { source?: ServerNode; [key: string]: unknown };
  [key: string]: unknown;
};

/**
 * Set servers.source.secrets.secret_name in a contract YAML string, creating the
 * `secrets` block (with type: core) when it doesn't exist. Throws if the YAML
 * doesn't have a `servers.source` object — DCM rejects contracts without one,
 * so callers shouldn't reach here on those.
 */
export function setContractSecretName( yamlContent: string, secretName: string ): string {
  const doc = yaml.load( yamlContent ) as ContractDoc | null | undefined;
  if ( !doc || typeof doc !== 'object' ) {
    throw new Error( 'Contract YAML is empty or invalid.' );
  }

  const servers = doc.servers;
  if ( !servers || typeof servers !== 'object' ) {
    throw new Error( 'Contract YAML has no servers block.' );
  }

  const source = servers.source;
  if ( !source || typeof source !== 'object' ) {
    throw new Error( 'Contract YAML has no servers.source block.' );
  }

  if ( !source.secrets || typeof source.secrets !== 'object' ) {
    source.secrets = { type: CORE_SECRET_TYPE, secret_name: secretName };
  } else {
    source.secrets.secret_name = secretName;
    if ( !source.secrets.type ) {
      source.secrets.type = CORE_SECRET_TYPE;
    }
  }

  return yaml.dump( doc, { noRefs: true, lineWidth: -1 } );
}

/**
 * Read the linked secret name from a parsed contract YAML, if any.
 * Returns null when the YAML has no servers.source.secrets.secret_name.
 */
export function getContractSecretName( yamlContent: string | undefined ): string | null {
  if ( !yamlContent ) return null;
  try {
    const doc = yaml.load( yamlContent ) as ContractDoc | null | undefined;
    const name = doc?.servers?.source?.secrets?.secret_name;
    return typeof name === 'string' && name.length > 0 ? name : null;
  } catch {
    return null;
  }
}
